/**
 * useApkBuild - Hook for BuildApkPage
 *
 * Manages Flutter and React Native APK build state, polling, and download logic.
 */
import { useState, useEffect } from 'react';
import { msg } from '../utils/messages';

type BuildStatus = 'idle' | 'building' | 'success' | 'error';

const getStoredReactState = () => {
  try {
    const stored = sessionStorage.getItem('reactBuildState');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error reading sessionStorage:', e);
  }
  return { buildId: null, status: 'idle', progress: 0, building: false };
};

export interface UseApkBuildReturn {
  buildingFlutter: boolean;
  flutterOutput: string;
  flutterStatus: BuildStatus;
  buildFlutterApk: () => Promise<void>;
  buildingReact: boolean;
  reactOutput: string;
  reactStatus: BuildStatus;
  reactProgress: number;
  buildReactApk: () => Promise<void>;
  downloadApk: (type: 'flutter' | 'react') => Promise<void>;
}

export const useApkBuild = (onFetchNetworkDebug: () => void): UseApkBuildReturn => {
  const storedReactState = getStoredReactState();

  const [buildingFlutter, setBuildingFlutter] = useState(false);
  const [flutterOutput, setFlutterOutput] = useState<string>('');
  const [flutterStatus, setFlutterStatus] = useState<BuildStatus>('idle');

  const [buildingReact, setBuildingReact] = useState<boolean>(storedReactState.building);
  const [reactOutput, setReactOutput] = useState<string>('');
  const [reactStatus, setReactStatus] = useState<BuildStatus>(storedReactState.status);
  const [reactBuildId, setReactBuildId] = useState<string | null>(storedReactState.buildId);
  const [reactProgress, setReactProgress] = useState<number>(storedReactState.progress);

  // Persist React build state to sessionStorage
  useEffect(() => {
    const state = {
      buildId: reactBuildId,
      status: reactStatus,
      progress: reactProgress,
      building: buildingReact,
    };
    sessionStorage.setItem('reactBuildState', JSON.stringify(state));
  }, [reactBuildId, reactStatus, reactProgress, buildingReact]);

  // Poll for React build logs
  useEffect(() => {
    if (!reactBuildId) return;
    if (reactStatus !== 'building') return;

    let intervalId: NodeJS.Timeout | null = null;

    intervalId = setInterval(async () => {
      try {
        const response = await fetch(`http://localhost:8001/api/build-logs/react/${reactBuildId}`);
        const data = await response.json();

        if (response.ok) {
          setReactOutput(data.logs || '');
          setReactProgress(data.progress || 0);

          if (data.status === 'success') {
            setReactStatus('success');
            setBuildingReact(false);
            onFetchNetworkDebug();
            if (intervalId) clearInterval(intervalId);
          } else if (data.status === 'error' || data.status === 'timeout') {
            setReactStatus('error');
            setBuildingReact(false);
            if (intervalId) clearInterval(intervalId);
            sessionStorage.removeItem('reactBuildState');
          }
        }
      } catch (error) {
        console.error('Error polling logs:', error);
      }
    }, 1500);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [reactBuildId, reactStatus]);

  const buildFlutterApk = async () => {
    setBuildingFlutter(true);
    setFlutterStatus('building');
    setFlutterOutput('🚀 Iniciando build do APK Flutter...\n');

    try {
      const response = await fetch('http://localhost:8001/api/build-apk-flutter');
      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setFlutterStatus('success');
        setFlutterOutput(data.output || 'APK Flutter gerado com sucesso!');
        onFetchNetworkDebug();
      } else {
        setFlutterStatus('error');
        setFlutterOutput(data.error || data.message || 'Erro ao gerar APK Flutter');
      }
    } catch (error) {
      setFlutterStatus('error');
      setFlutterOutput(`Erro na comunicação: ${error}`);
    } finally {
      setBuildingFlutter(false);
    }
  };

  const buildReactApk = async () => {
    sessionStorage.removeItem('reactBuildState');

    setBuildingReact(true);
    setReactStatus('building');
    setReactOutput('🚀 Iniciando build do APK React Native...\n');
    setReactProgress(0);

    try {
      const response = await fetch('http://localhost:8001/api/build-apk-react');
      const data = await response.json();

      if (response.ok && data.status === 'started') {
        setReactBuildId(data.build_id);
      } else {
        setReactStatus('error');
        setReactOutput(data.message || 'Erro ao iniciar build');
        setBuildingReact(false);
      }
    } catch (error) {
      setReactStatus('error');
      setReactOutput(`Erro na comunicação: ${error}`);
      setBuildingReact(false);
    }
  };

  const downloadApk = async (type: 'flutter' | 'react') => {
    try {
      const filename = type === 'flutter' ? 'logmed_flutter.apk' : 'logmed_react.apk';
      const url = `http://localhost:8001/api/download-apk/${type}`;

      const response = await fetch(url);
      if (!response.ok) {
        msg.error(`Erro ao baixar APK: ${response.statusText}`);
        return;
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Erro ao baixar APK:', error);
      msg.error(`Erro ao baixar APK: ${error}`);
    }
  };

  return {
    buildingFlutter,
    flutterOutput,
    flutterStatus,
    buildFlutterApk,
    buildingReact,
    reactOutput,
    reactStatus,
    reactProgress,
    buildReactApk,
    downloadApk,
  };
};
